import { IAppContext } from 'App/AppContext';
import {
  ModuleAssets,
  ModuleControls,
  ModuleOrg,
  ModuleProcessingActivities,
  ModuleRisks,
  ModuleStandards,
  ModuleThemes,
} from 'models/setting';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FeatureTypes, hasUserFeature } from 'services/Auth/featurePermissions';
import { isAuditModuleEnabledForTenant } from 'utils/internalAudit';
import { isProjectsModuleEnabledForTenant } from 'utils/projects';
import { SubscriptionTypes } from 'utils/subscription';
import { INavItemDefinition, NavGate, NavGroupCaptionId, NavItemId } from './navModel';

//
// The catalogue: every item the main menu can show, with its label, icon, route, the location
// segments it owns and its full visibility rule. navLayout.ts decides where an item sits; this file
// decides what it is and who may see it, so an item can move between groups without ever reaching
// the wrong audience.
//
// A hook rather than a plain table: the translation scanner counts a key as used only when it is
// spelled out inside a t() call, so every label is written out here.
//

const isPortalSubscription = (appContext: IAppContext): boolean =>
  appContext.user.login.subscriptionType === SubscriptionTypes.Portal;

/**
 * The licensing gate on the scrolling body of the menu: a signed-in, licensed user of a licensed
 * tenant who has finished onboarding and may see the dashboard. Portal subscriptions get the
 * administration area only.
 */
const isBodyVisible: NavGate = (appContext) =>
  appContext.isAuthenticated &&
  !isPortalSubscription(appContext) &&
  appContext.user.login.tenantLicensed &&
  appContext.user.login.userLicensed &&
  appContext.isOnboardingInProgress === false &&
  hasUserFeature(appContext, FeatureTypes.Dashboard);

/**
 * The gate on the pinned administration area. Wider than the body gate: an admin whose trial or
 * subscription ended keeps this area so they can subscribe again.
 */
const isAdminAreaVisible: NavGate = (appContext) =>
  appContext.isOnboardingInProgress === false &&
  (appContext.user.login.userLicensed ||
    (appContext.user.login.tenantId.length > 0 &&
      appContext.user.login.userLicensed === false &&
      appContext.user.login.tenantLicensed === false));

/** The pages of the Administration group that need the Admin feature. The header has a wider rule. */
const isAdminPageVisible: NavGate = (appContext) =>
  isAdminAreaVisible(appContext) && hasUserFeature(appContext, FeatureTypes.Admin);

/** The Partner group, header and pages. Registered at tenant level, so the whole group travels together. */
const isPartnerAreaVisible: NavGate = (appContext) =>
  isAdminAreaVisible(appContext) &&
  appContext.user.login.isPartner &&
  hasUserFeature(appContext, FeatureTypes.PartnerAdmin);

/** The Tenant Admin group, header and pages. */
const isTenantAdminAreaVisible: NavGate = (appContext) =>
  isAdminAreaVisible(appContext) && hasUserFeature(appContext, FeatureTypes.TenantAdmin);

/** Module settings are on unless switched off. Projects and Audit have their own helpers. */
const isModuleOn = (appContext: IAppContext, moduleKey: string): boolean =>
  (appContext.globalDataCache.settings.get(moduleKey) as boolean) !== false;

/** One rule for the Frameworks entry and its Overview view, so the two are visible together. */
const isFrameworksVisible: NavGate = (appContext) =>
  isBodyVisible(appContext) &&
  isModuleOn(appContext, ModuleStandards) &&
  hasUserFeature(appContext, FeatureTypes.Standards);

export const useNavGroupCaptions = (): Record<NavGroupCaptionId, string> => {
  const { t } = useTranslation(['translation']);

  return useMemo(
    () => ({
      start: t('translation:NavMain.Groups.Start'),
      organization: t('translation:NavMain.Groups.Organization'),
      riskManagement: t('translation:NavMain.Groups.RiskManagement'),
      planningAndWork: t('translation:NavMain.Groups.PlanningAndWork'),
    }),
    [t],
  );
};

export const useNavItems = (): Record<NavItemId, INavItemDefinition> => {
  const { t } = useTranslation(['translation']);

  return useMemo(() => {
    const items: Record<NavItemId, INavItemDefinition> = {
      start: {
        id: 'start',
        label: t('translation:NavMain.Dashboards'),
        iconName: 'GoToDashboard',
        className: 'isoplanner-nav-main-start',
        route: '/',
        matchSegments: [['dashboard']],
        isVisible: () => true,
      },

      //
      // Frameworks. Overview, Requirements and Controls nest under Standards in the layout. The
      // Overview owns the location: a click on Standards opens the view visited last.
      //
      themes: {
        id: 'themes',
        label: t('translation:NavMain.Requirements'),
        iconName: 'ButtonControl',
        className: 'isoplanner-nav-main-requirements',
        route: '/themes',
        matchSegments: [['themes'], ['theme']],
        isVisible: (appContext) =>
          isBodyVisible(appContext) &&
          isModuleOn(appContext, ModuleThemes) &&
          hasUserFeature(appContext, FeatureTypes.Requirements),
      },
      controls: {
        id: 'controls',
        label: t('translation:NavMain.Controls'),
        iconName: 'Bullseye',
        className: 'isoplanner-nav-main-controls',
        route: '/controls',
        matchSegments: [['controls'], ['control']],
        isVisible: (appContext) =>
          isBodyVisible(appContext) &&
          isModuleOn(appContext, ModuleControls) &&
          hasUserFeature(appContext, FeatureTypes.Controls),
      },
      standards: {
        id: 'standards',
        label: t('translation:NavMain.Norms'),
        iconName: 'MapLayers',
        className: 'isoplanner-nav-main-standards',
        // The page of a first click, before any view was visited.
        route: '/standards',
        matchSegments: [],
        isVisible: isFrameworksVisible,
      },
      'standards-overview': {
        id: 'standards-overview',
        label: t('translation:NavMain.FrameworksOverview'),
        iconName: 'BulletedTreeList',
        className: 'isoplanner-nav-main-standards-overview',
        route: '/standards',
        matchSegments: [['standards']],
        isVisible: isFrameworksVisible,
      },

      //
      // Risk management
      //
      risks: {
        id: 'risks',
        label: t('translation:NavMain.Risks'),
        iconName: 'Warning12',
        className: 'isoplanner-nav-main-risks',
        route: '/risks',
        matchSegments: [['risks'], ['risk']],
        isVisible: (appContext) =>
          isBodyVisible(appContext) &&
          isModuleOn(appContext, ModuleRisks) &&
          hasUserFeature(appContext, FeatureTypes.Risks),
      },
      assets: {
        id: 'assets',
        label: t('translation:NavMain.Assets'),
        iconName: 'FormLibrary',
        className: 'isoplanner-nav-main-assets',
        route: '/assets',
        matchSegments: [['assets'], ['asset']],
        isVisible: (appContext) =>
          isBodyVisible(appContext) &&
          isModuleOn(appContext, ModuleAssets) &&
          hasUserFeature(appContext, FeatureTypes.Assets),
      },
      processingactivities: {
        id: 'processingactivities',
        label: t('translation:NavMain.ProcessingActivities'),
        iconName: 'Shield',
        className: 'isoplanner-nav-main-processingactivities',
        route: '/processingactivities',
        matchSegments: [['processingactivities'], ['processingactivity']],
        isVisible: (appContext) =>
          isBodyVisible(appContext) &&
          isModuleOn(appContext, ModuleProcessingActivities) &&
          hasUserFeature(appContext, FeatureTypes.ProcessingActivities),
      },

      //
      // Planning and work
      //
      tasks: {
        id: 'tasks',
        label: t('translation:NavMain.Tasks'),
        iconName: 'TaskManager',
        className: 'isoplanner-nav-main-tasks',
        route: '/tasks',
        matchSegments: [['tasks']],
        isVisible: (appContext) => isBodyVisible(appContext) && hasUserFeature(appContext, FeatureTypes.Tasks),
      },
      schedule: {
        id: 'schedule',
        label: t('translation:NavMain.Schedule'),
        iconName: 'CalendarAgenda',
        className: 'isoplanner-nav-main-schedule',
        route: '/schedule',
        matchSegments: [['schedule']],
        isVisible: (appContext) => isBodyVisible(appContext) && hasUserFeature(appContext, FeatureTypes.AnnualPlan),
      },
      projects: {
        id: 'projects',
        label: t('translation:NavMain.Project'),
        iconName: 'MountainClimbing',
        className: 'isoplanner-nav-main-projects',
        route: '/project',
        matchSegments: [['projects'], ['project']],
        isVisible: (appContext) =>
          isBodyVisible(appContext) &&
          isProjectsModuleEnabledForTenant(appContext.globalDataCache.settings) &&
          hasUserFeature(appContext, FeatureTypes.Projects),
      },
      audits: {
        id: 'audits',
        label: t('translation:NavMain.Audit'),
        iconName: 'ComplianceAudit',
        className: 'isoplanner-nav-main-audits',
        route: '/audits',
        matchSegments: [['audits'], ['audit']],
        isVisible: (appContext) =>
          isBodyVisible(appContext) &&
          isAuditModuleEnabledForTenant(appContext.globalDataCache.settings) &&
          hasUserFeature(appContext, FeatureTypes.Audit),
      },

      //
      // Organization. Processes, Objectives and KPIs are three scenes of their own. They keep the
      // right of the Organization area alongside their own, as the module setting gates all three.
      //
      processes: {
        id: 'processes',
        label: t('translation:NavMain.Processes'),
        iconName: 'SplitObject',
        // The tour selector of the Organization area, kept on Processes now that the area is gone.
        className: 'isoplanner-nav-main-org',
        route: '/processes/overview',
        matchSegments: [['processes'], ['process']],
        isVisible: (appContext) =>
          isBodyVisible(appContext) &&
          isModuleOn(appContext, ModuleOrg) &&
          hasUserFeature(appContext, FeatureTypes.Organization) &&
          hasUserFeature(appContext, FeatureTypes.Processes),
      },
      objectives: {
        id: 'objectives',
        label: t('translation:NavMain.Objectives'),
        iconName: 'VersionControlPush',
        className: 'isoplanner-nav-main-objectives',
        route: '/objectives/overview',
        matchSegments: [['objectives'], ['objective']],
        isVisible: (appContext) =>
          isBodyVisible(appContext) &&
          isModuleOn(appContext, ModuleOrg) &&
          hasUserFeature(appContext, FeatureTypes.Organization) &&
          hasUserFeature(appContext, FeatureTypes.Objectives),
      },
      kpis: {
        id: 'kpis',
        label: t('translation:NavMain.KPIs'),
        iconName: 'Chart',
        className: 'isoplanner-nav-main-kpis',
        route: '/kpis/overview',
        matchSegments: [['kpis'], ['kpi']],
        isVisible: (appContext) =>
          isBodyVisible(appContext) &&
          isModuleOn(appContext, ModuleOrg) &&
          hasUserFeature(appContext, FeatureTypes.Organization) &&
          hasUserFeature(appContext, FeatureTypes.KPIsAndForms),
      },
      library: {
        id: 'library',
        label: t('translation:NavMain.Library'),
        iconName: 'DocumentSet',
        className: 'isoplanner-nav-main-library',
        route: '/library',
        matchSegments: [['library']],
        isVisible: (appContext) => isBodyVisible(appContext) && hasUserFeature(appContext, FeatureTypes.Library),
      },

      //
      // Pinned: Administration
      //
      admin: {
        id: 'admin',
        label: t('translation:NavAdmin.Title'),
        iconName: 'Admin',
        className: 'isoplanner-nav-main-admin',
        matchSegments: [],
        isVisible: (appContext) =>
          isAdminAreaVisible(appContext) &&
          (hasUserFeature(appContext, FeatureTypes.Admin) ||
            hasUserFeature(appContext, FeatureTypes.AdminOrgUnits) ||
            hasUserFeature(appContext, FeatureTypes.AdminAuth) ||
            hasUserFeature(appContext, FeatureTypes.AdminSubscription)),
      },
      'admin-reports': {
        id: 'admin-reports',
        label: t('translation:NavManagement.Reports'),
        iconName: 'ReportLibrary',
        className: 'isoplanner-nav-main-admin-reports',
        route: '/admin/reports',
        matchSegments: [['admin', 'reports']],
        isVisible: isAdminPageVisible,
      },
      'admin-store': {
        id: 'admin-store',
        label: t('translation:NavManagement.Store'),
        iconName: 'OfficeStoreLogo',
        className: 'isoplanner-nav-main-admin-store',
        route: '/admin/store',
        matchSegments: [['admin', 'store']],
        isVisible: isAdminPageVisible,
      },
      'admin-standards': {
        id: 'admin-standards',
        label: t('translation:NavManagement.Norms'),
        iconName: 'ProductList',
        className: 'isoplanner-nav-main-admin-standards',
        route: '/admin/standards',
        matchSegments: [['admin', 'standards']],
        isVisible: isAdminPageVisible,
      },
      'admin-settings': {
        id: 'admin-settings',
        label: t('translation:NavManagement.Settings'),
        iconName: 'Settings',
        className: 'isoplanner-nav-main-admin-settings',
        route: '/admin/settings',
        matchSegments: [['admin', 'settings']],
        isVisible: isAdminPageVisible,
      },
      'admin-groups': {
        id: 'admin-groups',
        label: t('translation:NavManagement.Groups'),
        iconName: 'AddGroup',
        className: 'isoplanner-nav-main-admin-groups',
        route: '/admin/groups',
        matchSegments: [['admin', 'groups']],
        isVisible: isAdminPageVisible,
      },
      'admin-content': {
        id: 'admin-content',
        label: t('translation:NavManagement.ContentConfig'),
        iconName: 'ExploreContent',
        route: '/admin/content',
        matchSegments: [['admin', 'content']],
        isVisible: isAdminPageVisible,
      },
      'admin-auth': {
        id: 'admin-auth',
        label: t('translation:NavAdmin.Authorization'),
        iconName: 'FabricUserFolder',
        className: 'isoplanner-nav-main-auth',
        route: '/admin/users',
        matchSegments: [['admin', 'users']],
        isVisible: (appContext) => isAdminAreaVisible(appContext) && hasUserFeature(appContext, FeatureTypes.AdminAuth),
      },
      'admin-subscription': {
        id: 'admin-subscription',
        label: t('translation:NavAdmin.Subscription'),
        iconName: 'Subscribe',
        className: 'isoplanner-nav-main-subscription',
        route: '/admin/subscription',
        matchSegments: [['admin', 'subscription']],
        isVisible: (appContext) =>
          isAdminAreaVisible(appContext) && hasUserFeature(appContext, FeatureTypes.AdminSubscription),
      },
      'admin-orgunits': {
        id: 'admin-orgunits',
        label: t('translation:NavAdmin.OrgUnits'),
        iconName: 'Org',
        className: 'isoplanner-nav-main-orgunits',
        route: '/admin/orgunits',
        matchSegments: [['admin', 'orgunits']],
        // License managers see the Admin group but cannot manage organizational units.
        isVisible: (appContext) =>
          isAdminAreaVisible(appContext) && hasUserFeature(appContext, FeatureTypes.AdminOrgUnits),
      },

      //
      // Pinned: Partner
      //
      partneradmin: {
        id: 'partneradmin',
        label: t('translation:NavPartnerAdmin.Title'),
        iconName: 'Teamwork',
        className: 'isoplanner-nav-main-partner',
        matchSegments: [],
        isVisible: isPartnerAreaVisible,
      },
      'partneradmin-customers': {
        id: 'partneradmin-customers',
        label: t('translation:NavPartnerAdmin.Customers'),
        iconName: 'PlayerSettings',
        className: 'isoplanner-nav-main-partner',
        route: '/partner/customers',
        matchSegments: [
          ['partner', 'customers'],
          ['partner', 'customer'],
        ],
        isVisible: isPartnerAreaVisible,
      },
      'partneradmin-dashboard': {
        id: 'partneradmin-dashboard',
        label: t('translation:NavPartnerAdmin.Dashboard'),
        iconName: 'ViewDashboard',
        className: 'isoplanner-nav-main-partner',
        route: '/partner/dashboard',
        matchSegments: [['partner', 'dashboard']],
        isVisible: isPartnerAreaVisible,
      },

      //
      // Pinned: Tenant Admin. Internal scenes, English labels by design (AGENTS.md).
      //
      tenantadmin: {
        id: 'tenantadmin',
        label: t('translation:NavTenantAdmin.Title'),
        iconName: 'ServerEnviroment',
        className: 'isoplanner-nav-main-tenantadmin',
        matchSegments: [],
        isVisible: isTenantAdminAreaVisible,
      },
      'tenantadmin-controls': {
        id: 'tenantadmin-controls',
        label: t('translation:NavTenantAdmin.Controls'),
        iconName: 'CreateMailRule',
        className: 'isoplanner-nav-main-tenantadmin',
        route: '/tenant/controls',
        matchSegments: [['tenant', 'controls']],
        isVisible: (appContext) =>
          isTenantAdminAreaVisible(appContext) && hasUserFeature(appContext, FeatureTypes.TenantISOAdmin),
      },
      'tenantadmin-manage': {
        id: 'tenantadmin-manage',
        label: t('translation:NavTenantAdmin.Manage'),
        iconName: 'FabricUserFolder',
        route: '/tenant/manage',
        matchSegments: [['tenant', 'manage']],
        isVisible: isTenantAdminAreaVisible,
      },
      'tenantadmin-logs': {
        id: 'tenantadmin-logs',
        label: t('translation:NavTenantAdmin.Logs'),
        iconName: 'SearchData',
        route: '/tenant/logs',
        matchSegments: [['tenant', 'logs']],
        isVisible: isTenantAdminAreaVisible,
      },
      'tenantadmin-ai': {
        id: 'tenantadmin-ai',
        label: 'AI Management',
        iconName: 'Robot',
        matchSegments: [],
        isVisible: isTenantAdminAreaVisible,
      },
      'tenantadmin-ai-trace': {
        id: 'tenantadmin-ai-trace',
        label: 'AI Trace',
        iconName: 'DiagnosticDataBarTooltip',
        route: '/tenant/ai-trace',
        matchSegments: [['tenant', 'ai-trace']],
        isVisible: isTenantAdminAreaVisible,
      },
      'tenantadmin-ai-audit-trace': {
        id: 'tenantadmin-ai-audit-trace',
        label: 'AI Audit Trace',
        iconName: 'ComplianceAudit',
        route: '/tenant/ai-audit-trace',
        matchSegments: [['tenant', 'ai-audit-trace']],
        isVisible: isTenantAdminAreaVisible,
      },
      'tenantadmin-ai-credits': {
        id: 'tenantadmin-ai-credits',
        label: 'AI Credits',
        iconName: 'Money',
        route: '/tenant/ai-credits',
        matchSegments: [['tenant', 'ai-credits']],
        isVisible: isTenantAdminAreaVisible,
      },
      'tenantadmin-identity-issuers': {
        id: 'tenantadmin-identity-issuers',
        label: 'Identity Issuers',
        iconName: 'Signin',
        route: '/tenant/identity-issuers',
        matchSegments: [['tenant', 'identity-issuers']],
        isVisible: isTenantAdminAreaVisible,
      },
      'tenantadmin-ai-instructions': {
        id: 'tenantadmin-ai-instructions',
        label: 'AI Instructions',
        iconName: 'AllApps',
        route: '/tenant/ai-instructions',
        matchSegments: [['tenant', 'ai-instructions']],
        isVisible: isTenantAdminAreaVisible,
      },
      'tenantadmin-ai-skills': {
        id: 'tenantadmin-ai-skills',
        label: 'AI Skills',
        iconName: 'Lightbulb',
        route: '/tenant/ai-skills',
        matchSegments: [['tenant', 'ai-skills']],
        isVisible: isTenantAdminAreaVisible,
      },
      'tenantadmin-documents': {
        id: 'tenantadmin-documents',
        label: 'AI Documents',
        iconName: 'KnowledgeArticle',
        route: '/tenant/documents',
        matchSegments: [['tenant', 'documents']],
        isVisible: isTenantAdminAreaVisible,
      },
      'tenantadmin-ai-standard-input': {
        id: 'tenantadmin-ai-standard-input',
        label: 'AI Standard Input',
        iconName: 'BranchMerge',
        route: '/tenant/ai-standard-input',
        matchSegments: [['tenant', 'ai-standard-input']],
        isVisible: isTenantAdminAreaVisible,
      },
      'tenantadmin-announcements': {
        id: 'tenantadmin-announcements',
        label: 'Announcements',
        iconName: 'Megaphone',
        route: '/tenant/announcements',
        matchSegments: [['tenant', 'announcements']],
        isVisible: isTenantAdminAreaVisible,
      },
    };

    return items;
  }, [t]);
};
